import { useCallback, useEffect, useState } from 'react';
import { isApiError } from '@aiptyg/types';
import * as settingsService from '../services/settings.service';
import type {
  AppUpdateConfigDTO,
  AppUpdateConfigUpdateParams,
  PlatformAppUpdateConfigDTO,
} from '../types/settings.types';
import { useToastStore } from '../../../store/toast.store';

const EMPTY_PLATFORM: PlatformAppUpdateConfigDTO = {
  enabled: false,
  min_version: '',
  force_update: false,
  download_url: '',
  title: '',
  message: '',
};

const EMPTY_FORM: AppUpdateConfigUpdateParams = {
  market_id: 'CN',
  ios: { ...EMPTY_PLATFORM },
  android: { ...EMPTY_PLATFORM },
};

export type AppUpdatePlatformKey = 'ios' | 'android';

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      network_error: '网络连接失败，请检查网络',
      'invalid app update config': 'App 更新配置无效，请检查启用平台的版本号和下载地址',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

function normalizePlatform(dto?: PlatformAppUpdateConfigDTO): PlatformAppUpdateConfigDTO {
  return {
    enabled: Boolean(dto?.enabled),
    min_version: dto?.min_version ?? '',
    force_update: Boolean(dto?.force_update),
    download_url: dto?.download_url ?? '',
    title: dto?.title ?? '',
    message: dto?.message ?? '',
  };
}

function dtoToForm(dto: AppUpdateConfigDTO, marketId = 'CN'): AppUpdateConfigUpdateParams {
  const target = marketId.trim() || 'CN';
  const item = dto.items?.find((entry) => (entry.market_id || 'CN') === target);
  return {
    market_id: target,
    ios: normalizePlatform(item?.ios ?? (target === 'CN' ? dto.ios : undefined)),
    android: normalizePlatform(item?.android ?? (target === 'CN' ? dto.android : undefined)),
  };
}

function validatePlatform(label: string, config: PlatformAppUpdateConfigDTO): string | null {
  if (!config.enabled) return null;
  if (!config.min_version.trim()) return `${label} 启用后必须填写最低版本号`;
  if (!/^[0-9]+(\.[0-9]+){0,3}$/.test(config.min_version.trim())) {
    return `${label} 最低版本号格式应为 1.0.1`;
  }
  if (!config.download_url.trim()) return `${label} 启用后必须填写下载地址`;
  if (!/^https?:\/\//.test(config.download_url.trim())) {
    return `${label} 下载地址必须以 http:// 或 https:// 开头`;
  }
  return null;
}

export function useAppUpdateConfig() {
  const addToast = useToastStore((s) => s.addToast);
  const [data, setData] = useState<AppUpdateConfigDTO | null>(null);
  const [form, setForm] = useState<AppUpdateConfigUpdateParams>(EMPTY_FORM);
  const [selectedMarketId, setSelectedMarketId] = useState('CN');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await settingsService.getAppUpdateConfig();
      setData(result);
      setForm(dtoToForm(result, selectedMarketId));
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, [selectedMarketId]);

  useEffect(() => {
    void load();
  }, [load]);

  const updatePlatformField = useCallback(<K extends keyof PlatformAppUpdateConfigDTO>(
    platform: AppUpdatePlatformKey,
    key: K,
    value: PlatformAppUpdateConfigDTO[K],
  ) => {
    setForm((prev) => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        [key]: value,
      },
    }));
  }, []);

  const selectMarket = useCallback((marketId: string) => {
    const nextMarketId = marketId.trim() || 'CN';
    setSelectedMarketId(nextMarketId);
    if (data) {
      setForm(dtoToForm(data, nextMarketId));
    } else {
      setForm((prev) => ({ ...prev, market_id: nextMarketId }));
    }
  }, [data]);

  const save = useCallback(async () => {
    const validationError =
      validatePlatform('iOS', form.ios) ?? validatePlatform('Android', form.android);
    if (validationError) {
      addToast('error', validationError);
      return false;
    }

    setSaving(true);
    try {
      const result = await settingsService.updateAppUpdateConfig({
        market_id: selectedMarketId,
        ios: {
          ...form.ios,
          min_version: form.ios.min_version.trim(),
          download_url: form.ios.download_url.trim(),
          title: form.ios.title.trim(),
          message: form.ios.message.trim(),
        },
        android: {
          ...form.android,
          min_version: form.android.min_version.trim(),
          download_url: form.android.download_url.trim(),
          title: form.android.title.trim(),
          message: form.android.message.trim(),
        },
      });
      setData(result);
      setForm(dtoToForm(result, selectedMarketId));
      addToast('success', 'App 更新配置已保存');
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSaving(false);
    }
  }, [addToast, form, selectedMarketId]);

  return {
    data,
    form,
    loading,
    saving,
    error,
    selectedMarketId,
    updatePlatformField,
    selectMarket,
    save,
    reload: load,
  };
}
